// track_action_sheet.js — action sheet with Playlists + Lyrics
// - Uses an internal view stack: { type: 'menu' | 'playlists' | 'lyrics', title }
// - Lyrics flow: try backend -> fallback to AUDD -> save to backend -> edit if still empty
// - Save: POST to Walmates API, then return to non-editable view
// - Cancel (in edit): pop back to the action sheet

import axios from 'axios';
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faPlus,
  faListUl,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { isSkippedPlaylist, SKIPPED_PLAYLIST_NAME } from './music_player_context';

// -----------------------------
// Utilities
// -----------------------------
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

// Mirror music_app.js: axios instance to Walmates
const server = axios.create({ baseURL: '/api' });

function authHeaders() {
  const token = localStorage.getItem('walmates-token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// -----------------------------
// Lyrics helpers
// -----------------------------
async function fetchLyricsFromBackend(video_id) {
  const res = await server.get('/music/lyrics/get', {
    params: { video_id },
    headers: authHeaders(),
  });
  return res.data; // { video_id, lyrics, updated }
}

async function upsertLyricsToBackend(video_id, lyrics) {
  const res = await server.post(
    '/music/lyrics/set',
    { video_id, lyrics },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return res.data; // { video_id, updated }
}

async function fetchLyricsFromAudd(query) {
  // NOTE: move this token server-side when you can.
  const token = '4829e4ce036c9569f83da8ab67e368fb';
  const url = `https://api.audd.io/findLyrics/?q=${encodeURIComponent(query)}&api_token=${token}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('audd failed');
  const data = await res.json();
  const txt = data?.result?.[0]?.lyrics;
  return txt || '';
}

// -----------------------------
// Component
// -----------------------------
export default function TrackActionSheet({
  isOpen,
  onClose,
  track,
  initialView = 'playlists',
  playlists = [],
  onAddToPlaylist,
  onGoToQueue,
}) {
  // Always-dark music UI
  const darkMode = true;
  const sheetRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  const dragging = useRef(false);

  const viewTitle = initialView === 'lyrics' ? 'Lyrics' : 'Add to Playlist';
  const [viewStack, setViewStack] = useState([{ type: initialView, title: viewTitle }]);
  const currentView = viewStack[viewStack.length - 1];

  // Lyrics state
  const defaultLyricsState = {
    loading: false,
    text: '',
    source: null, // 'db' | 'audd' | 'empty'
    edit: false,
    error: null,
    dirtyText: '',
    saving: false,
  };
  const [lyricsState, setLyricsState] = useState(defaultLyricsState);

  const openLyrics = useCallback(async () => {
    if (!track) return;
    const artist = track.artist || 'Unknown';
    const title = track.title || track.name || track.video_id;
    const query = `${title} ${artist}`;

    setLyricsState(s => ({ ...s, loading: true, text: '', error: null, edit: false, source: null, dirtyText: '' }));

    try {
      const db = await fetchLyricsFromBackend(track.video_id);
      const txt = (db?.lyrics || '').trim();

      if (txt) {
        setLyricsState({ ...defaultLyricsState, loading: false, text: txt, source: 'db' });
        return;
      }

      const auddText = (await fetchLyricsFromAudd(query)).trim();
      if (auddText) {
        setLyricsState({ ...defaultLyricsState, loading: false, text: auddText, source: 'audd' });
        upsertLyricsToBackend(track.video_id, auddText).catch(() => {});
        return;
      }

      setLyricsState({ ...defaultLyricsState, loading: false, source: 'empty', edit: true });
    } catch (err) {
      setLyricsState({ ...defaultLyricsState, loading: false, source: 'empty', edit: true, error: 'Unable to fetch lyrics right now.' });
    }
  }, [track]);

  useEffect(() => {
    if (!isOpen) return;
    const title = initialView === 'lyrics' ? 'Lyrics' : 'Add to Playlist';
    setViewStack([{ type: initialView, title }]);
    setLyricsState(defaultLyricsState);
    if (sheetRef.current) sheetRef.current.style.transform = 'translateX(-50%) translateY(0)';
    if (initialView === 'lyrics' && track) {
      openLyrics();
    }
  }, [isOpen, initialView, track, openLyrics]);

  // Drag-to-dismiss for touch
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;

    const handleTouchStart = (e) => {
      dragging.current = true;
      startY.current = e.touches[0].clientY;
    };
    const handleTouchMove = (e) => {
      if (!dragging.current) return;
      currentY.current = e.touches[0].clientY;
      const deltaY = clamp(currentY.current - startY.current, 0, window.innerHeight);
      sheet.style.transform = `translateX(-50%) translateY(${deltaY}px)`;
    };
    const handleTouchEnd = () => {
      dragging.current = false;
      const deltaY = currentY.current - startY.current;
      if (deltaY > 100) {
        onClose?.();
      } else if (sheet) {
        sheet.style.transform = 'translateX(-50%) translateY(0)';
      }
    };

    sheet.addEventListener('touchstart', handleTouchStart, { passive: true });
    sheet.addEventListener('touchmove', handleTouchMove, { passive: true });
    sheet.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      sheet.removeEventListener('touchstart', handleTouchStart);
      sheet.removeEventListener('touchmove', handleTouchMove);
      sheet.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onClose]);

  if (!track) return null;

  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : true;
  const bg = '#181818';
  const fg = '#f5f5f5';
  const sub = '#8a8a8a';
  const border = 'rgba(255,255,255,0.12)';
  const accent = 'var(--player-accent, #ff7a59)';

  const handleClose = () => onClose?.();
  const handleBack = () => handleClose();

  // -----------------------------
  // Views
  // -----------------------------
  const Header = () => (
    <div style={{
      position: 'sticky', top: 0, zIndex: 2,
      display: 'grid', gridTemplateColumns: 'auto 1fr auto', alignItems: 'center',
      gap: 10, padding: '10px 12px', minHeight: 52,
      borderBottom: `1px solid ${border}`, background: bg, color: fg,
    }}>
      <button
        onClick={handleBack}
        aria-label="Close"
        style={btnRoundStyle(fg)}
      >
        <FontAwesomeIcon icon={faChevronDown} />
      </button>

      <div style={{ fontWeight: 600, fontSize: 15, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {currentView.title}
      </div>

      <button
        onClick={() => { onGoToQueue?.(); onClose?.(); }}
        aria-label="Go to queue"
        title="Go to queue"
        style={btnRoundStyle(fg)}
      >
        <FontAwesomeIcon icon={faListUl} />
      </button>
    </div>
  );

  const TrackHeader = () => (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 12px' }}>
      <img
        src={track.thumbnail_medium || track.thumbnail_default}
        alt={track.title}
        style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }}
      />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{track.title}</div>
        <div style={{ fontSize: 12, color: sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{track.artist}</div>
      </div>
    </div>
  );

  const PlaylistsView = () => {
    const [newName, setNewName] = useState('');
    const selectablePlaylists = (playlists || []).filter((p) => !isSkippedPlaylist(p));
    const belonging = new Set(
      selectablePlaylists.filter((p) => p.trackIds?.includes?.(track.video_id)).map((p) => p.name)
    );

    const tryCreate = (raw) => {
      const trimmed = String(raw || '').trim();
      if (!trimmed) return;
      if (trimmed.localeCompare(SKIPPED_PLAYLIST_NAME, undefined, { sensitivity: 'accent' }) === 0) {
        return;
      }
      onAddToPlaylist?.(track.video_id, trimmed);
      setNewName('');
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 12px 16px' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New playlist"
            onKeyDown={(e) => {
              if (e.key === 'Enter') tryCreate(newName);
            }}
            style={inputStyle(bg, fg, border)}
          />
          <button
            onClick={() => tryCreate(newName)}
            style={pillBtnStyle(fg)}
            aria-label="Create playlist"
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', marginTop: 6 }}>
          {selectablePlaylists.map((p, i) => {
            const checked = belonging.has(p.name);
            return (
              <label
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '11px 4px',
                  borderBottom: `1px solid ${border}`,
                  color: fg,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onAddToPlaylist?.(track.video_id, p.name)}
                  style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                />
                <span
                  aria-hidden="true"
                  style={{
                    width: 22,
                    height: 22,
                    flexShrink: 0,
                    borderRadius: 7,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: checked ? `1.5px solid ${accent}` : '1.5px solid rgba(255,255,255,0.35)',
                    background: checked
                      ? `linear-gradient(145deg, ${accent}, color-mix(in srgb, ${accent} 72%, #000))`
                      : 'rgba(255,255,255,0.04)',
                    boxShadow: checked
                      ? `0 0 0 3px color-mix(in srgb, ${accent} 22%, transparent), 0 4px 10px color-mix(in srgb, ${accent} 28%, transparent)`
                      : 'inset 0 1px 0 rgba(255,255,255,0.06)',
                    color: '#0b0b0f',
                    fontSize: 11,
                    transition: 'background .15s ease, border-color .15s ease, box-shadow .15s ease, transform .12s ease',
                    transform: checked ? 'scale(1)' : 'scale(0.98)',
                  }}
                >
                  {checked && <FontAwesomeIcon icon={faCheck} />}
                </span>
                <span style={{ fontSize: 14, fontWeight: checked ? 600 : 450 }}>{p.name}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  // ---------------------------------
  // Lyrics view
  // ---------------------------------
  const LyricsView = () => {
    const { loading, text, source, edit, error, dirtyText, saving } = lyricsState;

    const startEdit = () => {
      setLyricsState(s => ({ ...s, edit: true, dirtyText: s.text ?? '', error: null }));
    };

    // CANCEL: go back to the action sheet
    const cancelEdit = () => {
      handleClose();
    };

    // SAVE: send to server, then make non-editable
    const saveEdit = async () => {
      const newLyrics = (lyricsState.dirtyText || '').trim();
      try {
        setLyricsState(s => ({ ...s, saving: true, error: null }));
        await upsertLyricsToBackend(track.video_id, newLyrics);
        setLyricsState(s => ({
          ...s,
          text: newLyrics,
          edit: false,      // read-only after save
          saving: false,
          source: 'db',
          dirtyText: ''
        }));
      } catch (e) {
        setLyricsState(s => ({ ...s, saving: false, error: 'Saving failed. Please try again.' }));
      }
    };
    return (
      <div style={{ padding: '10px 12px 16px' }}>
      {loading ? (
        <div style={{ color: sub, padding: '12px 6px' }}>Fetching lyrics…</div>
      ) : (
        <>
        {error && (
          <div style={{
            marginBottom: 8, padding: '8px 10px', borderRadius: 8,
              border: `1px solid ${border}`,
              color: darkMode ? '#fca5a5' : '#991b1b',
              background: darkMode ? '#2a1212' : '#ffe9e9'
          }}>
          {error}
          </div>
        )}
        {edit ? (
          <>
          {/* EDIT MODE */}
          <textarea
          value={dirtyText}
          onChange={(e) => setLyricsState(s => ({ ...s, dirtyText: e.target.value }))}
          placeholder="Type or paste lyrics here…"
          disabled={saving}
          autoFocus
          style={{
            width: '100%', minHeight: '36vh',
              background: darkMode ? '#1e1e1e' : '#f9f9f9',
              color: darkMode ? '#f5f5f5' : '#222',
              padding: '12px 14px',
              borderRadius: 12,
              border: `1px solid ${border}`,
              lineHeight: 1.6,
              fontFamily: 'Georgia, serif',
              fontSize: 15,
              resize: 'vertical',
              whiteSpace: 'pre-wrap',
              opacity: saving ? 0.7 : 1
          }}
          />

          {/* Always-visible action bar */}
          <div style={{
            display: 'flex',
              gap: 10,
              justifyContent: 'flex-end',
              alignItems: 'center',
              paddingTop: 12,
              marginTop: 12,
              borderTop: `1px solid ${border}`
          }}>
          <button
          onClick={cancelEdit}
          disabled={saving}
          style={solidBtnStyle(darkMode ? '#e5e7eb' : '#374151', darkMode ? '#232323' : '#f3f4f6')}
          >
          Cancel
          </button>
          <button
          onClick={saveEdit}
          disabled={saving}
          style={solidBtnStyle('#ffffff', '#3b82f6')}
          >
          {saving ? 'Saving…' : 'Save'}
          </button>
          </div>
          </>
        ) : (
          // READ-ONLY (unchanged)
          <>
          <div style={{
            maxHeight: isMobile ? '50vh' : '40vh',
              overflowY: 'auto',
              background: darkMode ? '#1e1e1e' : '#f9f9f9',
              color: darkMode ? '#f5f5f5' : '#222',
              padding: '12px 14px',
              borderRadius: 12,
              border: `1px solid ${border}`,
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6,
              fontFamily: 'Georgia, serif',
              fontSize: 15
          }}>
          {text || 'No lyrics yet.'}
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:10, color: sub, fontSize:12 }}>
          <span>
          {source === 'db' && 'Loaded from library'}
          {source === 'audd' && 'Auto-fetched via AUDD'}
          {source === 'empty' && 'No lyrics found'}
          </span>
          <div style={{ display:'flex', gap: 8 }}>
          {/* Done: close lyrics and go back to the action sheet */}
          <button
          onClick={handleClose}
          style={solidBtnStyle(darkMode ? '#e5e7eb' : '#374151', darkMode ? '#232323' : '#f3f4f6')}
          aria-label="Done"
          title="Done"
          >
          Done
          </button>

          {/* Edit (or Add Lyrics if empty) */}
          <button
          onClick={() => setLyricsState(s => ({ ...s, edit: true, dirtyText: s.text ?? '' }))}
          style={solidBtnStyle('#ffffff', '#6b7280')}
          aria-label={text ? 'Edit' : 'Add Lyrics'}
          title={text ? 'Edit' : 'Add Lyrics'}
          >
          {text ? 'Edit' : 'Add Lyrics'}
          </button>
          </div>
          </div>
          </>
        )}
        </>
      )}
      </div>
    );

  };

  // -----------------------------
  // Render (portaled so outside clicks never hit track tiles)
  // -----------------------------
  if (!isOpen) return null;

  const swallowOutside = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleClose();
  };

  const modal = (
    <>
      <div
        className="track-action-sheet-backdrop"
        aria-hidden="true"
        onPointerDown={swallowOutside}
        onMouseDown={swallowOutside}
        onClick={swallowOutside}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(2px)',
          zIndex: 10060,
        }}
      />
      <div
        ref={sheetRef}
        className={`track-action-sheet ${isOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Track actions"
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          left: '50%',
          right: 'auto',
          bottom: isMobile ? 12 : 24,
          width: `min(${isMobile ? 360 : 420}px, calc(100vw - 24px))`,
          height: isMobile ? 'min(78vh, 640px)' : 'min(72vh, 620px)',
          maxHeight: isMobile ? '78vh' : '72vh',
          background: bg,
          color: fg,
          borderRadius: 18,
          border: `1px solid ${border}`,
          boxShadow: '0 18px 48px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04)',
          zIndex: 10061,
          transform: 'translateX(-50%)',
          transition: 'transform .3s ease, opacity .2s ease',
          overflow: 'hidden',
          padding: 0,
        }}
      >
        <div style={{ height: 5, width: 40, borderRadius: 3, background: border, margin: '10px auto 4px', flexShrink: 0 }} />

        <Header />
        <TrackHeader />

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {currentView.type === 'playlists' && <PlaylistsView />}
          {currentView.type === 'lyrics' && <LyricsView />}
        </div>
      </div>
    </>
  );

  return createPortal(modal, document.body);
}

// -----------------------------
// Small UI helpers
// -----------------------------
function btnRoundStyle(color) {
  return {
    width: 36, height: 36, borderRadius: 999,
    border: `2px solid ${color}`, background: 'transparent', color,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
  };
}

function pillBtnStyle(color) {
  return {
    padding: '8px 10px', borderRadius: 999, border: `1px solid ${color}55`,
    background: 'transparent', color, cursor: 'pointer',
  };
}

function inputStyle(bg, fg, border) {
  return {
    flex: 1,
    padding: '8px 12px',
    borderRadius: 8,
    border: `1px solid ${border}`,
    background: bg,
    color: fg,
    outline: 'none',
  };
}

function solidBtnStyle(fg, bg) {
  return {
    padding: '10px 14px',
    borderRadius: 10,
    border: 'none',
    background: bg,
    color: fg,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    minWidth: 88,
  };
}
